import { Action } from 'store/store';
import Order, { OrderColumns } from 'communication/types/order/Order';
import { OrderStreamUpdatedEvent } from './orderStreamsUpdatedEvent';
import { OrderFilterResultsChangedEvent } from './orderFilterResultsChangedEvent';
import { AnyAction } from 'redux';

export enum OrdersActionType {
    ORDERS_STREAM_SET_BATCH_UPDATE_IN_PROGRESS = 'ORDERS_STREAM_SET_BATCH_UPDATE_IN_PROGRESS',
    ORDERS_SET_ORDERS = 'ORDERS_SET_ORDERS',
    ORDERS_WATCH_STREAM = 'ORDERS_WATCH_STREAM',
    ORDERS_UNWATCH_COLUMNS = 'ORDERS_UNWATCH_COLUMNS',
    ORDERS_UPDATE_STREAMING_COLUMNS = 'ORDERS_UPDATE_STREAMING_COLUMNS',
    ORDERS_UPDATE_STREAMING_ORDER_IDS = 'ORDERS_UPDATE_STREAMING_ORDER_IDS',
    ORDERS_STREAM_UPDATED = 'ORDERS_STREAM_UPDATED',
    ORDERS_FILTER_RESULTS_CHANGED = 'ORDERS_FILTER_RESULTS_CHANGED',
    ORDERS_STREAM_CLEAR = 'ORDERS_STREAM_CLEAR',
}

export type OrdersStreamSetBatchUpdateInProgressAction =
    Action<OrdersActionType.ORDERS_STREAM_SET_BATCH_UPDATE_IN_PROGRESS>;

export const ordersStreamSetBatchUpdateInProgressAction: () => OrdersStreamSetBatchUpdateInProgressAction =
    () => ({
        type: OrdersActionType.ORDERS_STREAM_SET_BATCH_UPDATE_IN_PROGRESS,
    });

export interface OrdersSetOrdersActionPayload {
    orders: Order[];
    batchUpdateInProgress: boolean;
}

export type OrdersSetOrdersAction = Action<
    OrdersActionType.ORDERS_SET_ORDERS,
    OrdersSetOrdersActionPayload
>;

export const setOrdersAction: (
    orders: Order[],
    batchUpdateInProgress: boolean
) => OrdersSetOrdersAction = (orders, batchUpdateInProgress) => ({
    type: OrdersActionType.ORDERS_SET_ORDERS,
    payload: { orders, batchUpdateInProgress },
});

export type OrdersActionWatchStream = Action<
    OrdersActionType.ORDERS_WATCH_STREAM,
    { streamKey: string }
>;

export type OrdersActionUnwatchColumns = Action<
    OrdersActionType.ORDERS_UNWATCH_COLUMNS,
    { remainingColumns: OrderColumns }
>;

export const unwatchOrderColumns: (
    columns: OrderColumns
) => OrdersActionUnwatchColumns = (columns) => ({
    type: OrdersActionType.ORDERS_UNWATCH_COLUMNS,
    payload: { remainingColumns: columns },
});

export type OrdersUpdateStreamingColumns = Action<
    OrdersActionType.ORDERS_UPDATE_STREAMING_COLUMNS,
    OrderColumns
>;

export const updateStreamingColumns: (
    columns: OrderColumns
) => OrdersUpdateStreamingColumns = (columns) => ({
    type: OrdersActionType.ORDERS_UPDATE_STREAMING_COLUMNS,
    payload: columns,
});

export type OrdersUpdateStreamingOrderIds = Action<
    OrdersActionType.ORDERS_UPDATE_STREAMING_ORDER_IDS,
    string[]
>;

export const updateStreamingOrderIds: (
    orderIds: string[]
) => OrdersUpdateStreamingOrderIds = (orderIds) => ({
    type: OrdersActionType.ORDERS_UPDATE_STREAMING_ORDER_IDS,
    payload: orderIds,
});

export type OrdersStreamUpdated = Action<
    OrdersActionType.ORDERS_STREAM_UPDATED,
    OrderStreamUpdatedEvent
>;

/*
 * Dispatched asyncronously via a Loop returned from the reducer, and consumed by filter middleware
 */
export const ordersStreamUpdated: (
    payload: OrderStreamUpdatedEvent
) => OrdersStreamUpdated = (payload) => ({
    type: OrdersActionType.ORDERS_STREAM_UPDATED,
    payload,
});

export function isOrdersStreamUpdatedAction(
    action: AnyAction
): action is OrdersStreamUpdated {
    return action.type === OrdersActionType.ORDERS_STREAM_UPDATED;
}

/*
 * "Event" action indicating that there has been a change to an order that belongs to the filtered results of the currently active Blotter
 * Consumed by middleware for widgets such as Portfolio Returns and Portfolio Summary
 */
export type OrdersFilterResultsChanged = Action<
    OrdersActionType.ORDERS_FILTER_RESULTS_CHANGED,
    OrderFilterResultsChangedEvent
>;

export const ordersFilterResultsChanged: (
    payload: OrderFilterResultsChangedEvent
) => OrdersFilterResultsChanged = (payload) => ({
    type: OrdersActionType.ORDERS_FILTER_RESULTS_CHANGED,
    payload,
});

export function isOrdersFilterResultsChangedAction(
    action: AnyAction
): action is OrdersFilterResultsChanged {
    return action.type === OrdersActionType.ORDERS_FILTER_RESULTS_CHANGED;
}

export type OrdersStreamClearAction =
    Action<OrdersActionType.ORDERS_STREAM_CLEAR>;

export const ordersStreamClear: () => OrdersStreamClearAction = () => ({
    type: OrdersActionType.ORDERS_STREAM_CLEAR,
});

export type OrdersAction =
    | OrdersStreamSetBatchUpdateInProgressAction
    | OrdersSetOrdersAction
    | OrdersActionWatchStream
    | OrdersActionUnwatchColumns
    | OrdersUpdateStreamingColumns
    | OrdersUpdateStreamingOrderIds
    | OrdersStreamClearAction;

export function isOrderStreamUpdatedRequiringFilterRecalculation(
    action: AnyAction
): boolean {
    if (action.type !== OrdersActionType.ORDERS_STREAM_UPDATED) {
        return false;
    }
    const orderStreamUpdatedEvent = action.payload! as OrderStreamUpdatedEvent;

    return (
        orderStreamUpdatedEvent.completesRefresh ||
        orderStreamUpdatedEvent.cleared
    );
}
